/**
 * 209's follow-up (lane C's walkable tops, core/stands.ts and core/standGround.ts): the skirt round a stand's deck, drawn.
 * The sim makes the ground round a stand's footprint a skirt `skirt` wide, falling from its top to the ground beyond — a
 * step where the top stands within a step of the lowest ground round its rim, else a ramp — and a person walking up it is
 * drawn on its slope; this draws that slope. Its surface is the sim's own (`skirtHeight`, standGround's ramp without its
 * floor), sampled round the footprint's rounded rim and out across the skirt, over the ground without the stands; a lip at
 * its outer edge sinks under the drawn ground, so a drawn ground lower than the sim's (the flattening round a pad, the far
 * ground's lift) shows no gap. Where it is a ramp it is rammed earth laid in treads; where a step, a kerb. Any stand: the
 * landing pad's deck first, lane F's building footings next. Reads the stands, writes nothing; one mesh (one draw) a stand.
 */
import * as THREE from 'three';
import type { PlanetState, Stand, Vec3 } from '../types';
import { groundAt } from '../core/fineTerrain';
import { PERSON_BODY_HEIGHT, type PersonSet } from '../core/scaleProfile';
import { STAND } from '../constants/action';
import { markShared, disposeObjectTree } from './meshBake';

/** The look (colours; people for lengths): rammed earth in treads `tread` deep, a kerb's stone, the sunk lip and the sampling. */
export const SKIRT_LOOK = {
  earth: '#7c6449', earthDark: '#67533c', lip: '#8e7c62', kerb: '#8a8983', kerbTop: '#9d9c95',
  jitter: 0.06, tread: 0.35, sink: 0.8, side: 0.5, cornerSegs: 4,
} as const;

/**
 * The skirt's surface at unit direction (ux, uy, uz): standGround's ramp from the top to `ground` across the skirt, without
 * its floor at the ground (so where the ground stands higher it is under it, never on it), or null off the skirt.
 */
export function skirtHeight(s: Stand, radius: number, ux: number, uy: number, uz: number, ground: number): number | null {
  const lx = (ux * s.tx.x + uy * s.tx.y + uz * s.tx.z) * radius, lz = (ux * s.tz.x + uy * s.tz.y + uz * s.tz.z) * radius;
  const ex = Math.max(0, Math.abs(lx) - s.halfX), ez = Math.max(0, Math.abs(lz) - s.halfZ);
  const e = ex === 0 ? ez : ez === 0 ? ex : Math.sqrt(ex * ex + ez * ez);
  return e > s.skirt * (1 + 1e-9) ? null : s.top + (ground - s.top) * Math.min(1, e / s.skirt);
}

/** A step stand (its skirt the step's), drawn as a kerb; else a ramp. */
export function isKerb(s: Stand, person: number): boolean {
  return s.skirt <= STAND.stepSkirtPeople * person * (1 + 1e-6);
}

/** The rim walked round: each column's point on the footprint's edge (its frame) and its outward normal there. */
function rimColumns(s: Stand, person: number): { x: number; z: number; nx: number; nz: number }[] {
  const cols: { x: number; z: number; nx: number; nz: number }[] = [];
  const corners: [number, number][] = [[1, 1], [-1, 1], [-1, -1], [1, -1]];
  for (let c = 0; c < 4; c++) {
    const [ax, az] = corners[c], [bx, bz] = corners[(c + 1) % 4];
    // the side from corner a to corner b (its normal the axis they share), then the quarter round b
    const nx = ax === bx ? ax : 0, nz = az === bz ? az : 0;
    const len = nx ? 2 * s.halfZ : 2 * s.halfX, n = Math.max(1, Math.ceil(len / (SKIRT_LOOK.side * person)));
    for (let i = 0; i < n; i++) {
      const t = i / n;
      cols.push({ x: (ax + (bx - ax) * t) * s.halfX, z: (az + (bz - az) * t) * s.halfZ, nx, nz });
    }
    const a0 = Math.atan2(nz, nx);
    for (let i = 0; i < SKIRT_LOOK.cornerSegs; i++) {
      const a = a0 + (Math.PI / 2) * (i / SKIRT_LOOK.cornerSegs);
      cols.push({ x: bx * s.halfX, z: bz * s.halfZ, nx: Math.cos(a), nz: Math.sin(a) });
    }
  }
  return cols;
}

const _c = new THREE.Color();
/** Stand `s`'s skirt on `planet` as a geometry about its anchor (the planet's centre + its dir × its top); null without fine ground. */
export function skirtGeometry(s: Stand, planet: PlanetState, ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k' | 'materialRelief'>): THREE.BufferGeometry | null {
  if (!(ps.fineAmplitude > 0)) return null;
  const person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  const kerb = isKerb(s, person), rows = kerb ? 1 : Math.max(2, Math.ceil(s.skirt / (SKIRT_LOOK.tread * person)));
  const cols = rimColumns(s, person), C = cols.length, ax = s.dir.x * s.top, ay = s.dir.y * s.top, az = s.dir.z * s.top;
  // each column's points out across the skirt, then the lip sunk under its last; about the anchor
  const pts = new Float32Array(C * (rows + 2) * 3), u: Vec3 = { x: 0, y: 0, z: 0 };
  for (let c = 0; c < C; c++) {
    const col = cols[c];
    for (let r = 0; r <= rows + 1; r++) {
      const e = s.skirt * Math.min(1, r / rows), lx = col.x + col.nx * e, lz = col.z + col.nz * e;
      const dx = s.dir.x + (s.tx.x * lx + s.tz.x * lz) / R, dy = s.dir.y + (s.tx.y * lx + s.tz.y * lz) / R, dz = s.dir.z + (s.tx.z * lx + s.tz.z * lz) / R;
      const l = Math.sqrt(dx * dx + dy * dy + dz * dz);
      u.x = dx / l; u.y = dy / l; u.z = dz / l;
      const ground = groundAt(planet, u, 'ground', ps);   // (71 step 0: without the decks)
      const h = r > rows ? ground - SKIRT_LOOK.sink * person : skirtHeight(s, R, u.x, u.y, u.z, ground) ?? ground;
      const k = (c * (rows + 2) + r) * 3;
      pts[k] = u.x * h - ax; pts[k + 1] = u.y * h - ay; pts[k + 2] = u.z * h - az;
    }
  }
  // quads, each its own colour (flat: the treads crisp), wound to face out of the planet
  const quads = C * (rows + 1), pos = new Float32Array(quads * 18), col = new Float32Array(quads * 18);
  let q = 0;
  for (let c = 0; c < C; c++) {
    const c1 = (c + 1) % C, jit = 1 + SKIRT_LOOK.jitter * (((Math.imul(c + 1, 2654435761) >>> 0) % 1000) / 500 - 1);
    for (let r = 0; r <= rows; r++) {
      const shade = r === rows ? SKIRT_LOOK.earthDark
        : kerb ? SKIRT_LOOK.kerb : r === 0 ? SKIRT_LOOK.lip : r % 2 ? SKIRT_LOOK.earthDark : SKIRT_LOOK.earth;
      _c.set(r === 0 && kerb ? SKIRT_LOOK.kerbTop : shade).multiplyScalar(jit);
      const a = c * (rows + 2) + r, b = c1 * (rows + 2) + r;
      for (const v of [a, a + 1, b + 1, a, b + 1, b]) {
        pos.set(pts.subarray(v * 3, v * 3 + 3), q * 3); col[q * 3] = _c.r; col[q * 3 + 1] = _c.g; col[q * 3 + 2] = _c.b; q++;
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.computeVertexNormals();
  // (the winding: out of the planet — the faces' normals, summed, along the stand's up; else flipped, the whole at once)
  const n = g.getAttribute('normal') as THREE.BufferAttribute;
  let up = 0;
  for (let i = 0; i < n.count; i += 3) up += n.getX(i) * s.dir.x + n.getY(i) * s.dir.y + n.getZ(i) * s.dir.z;
  if (up < 0) {
    for (let i = 0; i < pos.length; i += 9) for (let k = 0; k < 3; k++) { const t = pos[i + 3 + k]; pos[i + 3 + k] = pos[i + 6 + k]; pos[i + 6 + k] = t; }
    g.computeVertexNormals();
  }
  g.computeBoundingSphere();
  return g;
}

const skirtMat = markShared(new THREE.MeshLambertMaterial({ color: '#ffffff', vertexColors: true, flatShading: true }));

/** The skirts drawn: one mesh a stand, built the first frame its stand is laid, shown with its structure. */
export class StandSkirts {
  private readonly drawn = new Map<number, { stand: Stand; mesh: THREE.Mesh | null; planet: number }>();
  /** The planets' stand lists last laid (the same arrays: nothing to build or drop, a frame's work the shows and the places). */
  private laid: (readonly Stand[] | undefined)[] = [];
  constructor(private readonly scene: THREE.Object3D) {}

  /** Each frame: the planets' stands (a stand's id its structure's), the person set, and whether its structure is shown. */
  sync(planets: readonly PlanetState[], ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k' | 'materialRelief'>, shown: (id: number) => boolean): void {
    if (planets.length !== this.laid.length || planets.some((p, i) => p.stands !== this.laid[i])) this.lay(planets, ps);
    for (const [id, d] of this.drawn) {
      if (!d.mesh) continue;
      const s = d.stand, at = planets[d.planet].position;
      d.mesh.visible = shown(id);
      d.mesh.position.set(at.x + s.dir.x * s.top, at.y + s.dir.y * s.top, at.z + s.dir.z * s.top);
      d.mesh.updateMatrix();
    }
  }

  /** Build the stands new to the lists, drop the ones gone from them. */
  private lay(planets: readonly PlanetState[], ps: Pick<PersonSet, 'fineAmplitude' | 'fineWavelength' | 'k' | 'materialRelief'>): void {
    const seen = new Set<number>();
    planets.forEach((planet, pi) => {
      for (const s of planet.stands ?? []) {
        seen.add(s.id);
        const d = this.drawn.get(s.id);
        if (d && d.stand === s) continue;
        if (d) this.drop(s.id);
        const g = skirtGeometry(s, planet, ps), mesh = g ? new THREE.Mesh(g, skirtMat) : null;
        if (mesh) { mesh.name = 'standSkirt'; mesh.matrixAutoUpdate = false; this.scene.add(mesh); }
        this.drawn.set(s.id, { stand: s, mesh, planet: pi });
      }
    });
    for (const id of [...this.drawn.keys()]) if (!seen.has(id)) this.drop(id);
    this.laid = planets.map(p => p.stands);
  }

  /** How many skirts are drawn (for the cost note and the tests). */
  get count(): number { let n = 0; for (const d of this.drawn.values()) if (d.mesh) n++; return n; }

  private drop(id: number): void {
    const d = this.drawn.get(id);
    if (d?.mesh) disposeObjectTree(d.mesh);   // (the material is shared: kept)
    d?.mesh?.removeFromParent();
    this.drawn.delete(id);
  }

  dispose(): void { for (const id of [...this.drawn.keys()]) this.drop(id); this.laid = []; }
}
